import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import fastDeepEqual from 'fast-deep-equal/es6'; // this one can compare maps
import { useFeatureMenu } from 'features/menu/hooks/use-feature-menu';
import { useStoreContext } from 'features/store';
import { normalizeAvailableDayParts } from 'features/store/utils/day-part-utils';

import { IBaseProps } from '@rbi-ctg/frontend';
import { MenuObject } from '@rbi-ctg/menu';
import { useLogMenuExperimentExposure } from 'experiments/menu-variant/use-log-menu-experiment-exposure';
import {
  Channel,
  IEntity,
  IStoreMenuQuery,
  PosDataServiceMode,
  useStoreMenuLazyQuery,
} from 'generated/graphql-gateway';
import { useGetMenuSectionsLazyQuery } from 'generated/sanity-graphql';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useStableCallback } from 'hooks/use-stable-callback';
import { useBrazeContext } from 'state/braze';
import { withLazySimpleCache } from 'state/graphql/with-simple-cache';
import { useLocale } from 'state/intl';
import { LANGUAGES } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useDerivedServiceModeContext } from 'state/service-mode/derived';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { GraphQLErrorCodes, parseGraphQLErrorCodes } from 'utils/errors';
import logger from 'utils/logger';
import noop from 'utils/noop';

import useActiveDayParts, { isValidDayPart } from './hooks/use-active-day-parts';
import { IDayPartBoundary, IMainMenuContext, IValidDayPart } from './types';
import { remapDayParts } from './utils';

export const MainMenuContext = React.createContext<IMainMenuContext>({
  data: undefined,
  loading: false,
  getStoreMenu: () => Promise.resolve(),
  getMainMenu: () => false,
  getPricingAndAvailability: () => null,
  getCalories: () => null,
  getStoreMenuEntity: () => null,
  hasError: false,
  getSection: () => null,
  activeDayParts: [],
  forceRefreshActiveDayParts: noop,
  dayParts: [],
  isDayPartsSourceResolved: false,
  storeMenuLoading: false,
  storeMenuCalled: false,
  storeMenuData: undefined,
  setIsOffer: isOffer => isOffer,
  isOffer: false,
  isMenuLoading: false,
});

const useCachedGetMenuSectionsLazyQuery = withLazySimpleCache(useGetMenuSectionsLazyQuery);
const useCachedStoreMenuLazyQuery = withLazySimpleCache(useStoreMenuLazyQuery);
const EMPTY_ACTIVE_DAY_PARTS: IDayPartBoundary[] = [];
const EMPTY_MENU_DAY_PARTS: IValidDayPart[] = [];

interface ILoadedStoreMenuVariant {
  requestedVariantName: string | null;
  resolvedVariantName: string | null;
  storeId: string;
}

export const MainMenuProvider: FC<React.PropsWithChildren<IBaseProps>> = ({ children }) => {
  const storeDayPartsEnabled = useFlag(LaunchDarklyFlag.ENABLE_DAY_PARTS_PER_STORE);
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const { isDelivery } = useDerivedServiceModeContext();
  const { refetchPosData, availableDayParts, noStoreSelected, store } = useStoreContext();
  const {
    activeDayParts: unifiedMenuActiveDayParts,
    dayParts: unifiedMenuDayParts,
    isDayPartsSourceResolved: isUnifiedMenuDayPartsSourceResolved,
  } = useUnifiedMenuContext();
  const storeInfoActiveDayParts = normalizeAvailableDayParts(availableDayParts);
  const isLegacyMenuEnabled = Boolean(store?.number) && !noStoreSelected && !isUnifiedMenuEnabled;

  const {
    featureMenu,
    featureMenuLoading,
    isMenuExperimentVariantSelected,
    menuExperimentExposureFlagKey,
    menuExperimentExposurePathname,
    menuId,
    variantName,
  } = useFeatureMenu({
    // Feature Menu is Legacy-only; One Menu day parts come from Store Information / payload.
    isEnabled: isLegacyMenuEnabled,
  });

  const { language } = useLocale();
  const prevLanguageRef = React.useRef<LANGUAGES>(language);

  const [menuCompletePending, setMenuCompletePending] = useState<boolean>(false);
  const [storeMenuCompletePending, setStoreMenuCompletePending] = useState<boolean>(false);

  const [menuSectionMap, setMenuSectionMap] = useState<Map<string, any>>(new Map());
  const [getMenuSectionsLazy, { loading, error, data }] = useCachedGetMenuSectionsLazyQuery({
    onCompleted(data) {
      setMenuSectionMap(oldValue => {
        const newValue = new Map(data.Menu?.options?.map(x => [(x as any)._id, x]));
        return fastDeepEqual(newValue, oldValue) ? oldValue : newValue;
      });
      setMenuCompletePending(false);
    },
    onError() {
      setMenuCompletePending(false);
    },
  });

  const [storeMenuDataMap, setStoreMenuDataMap] = useState<Map<string, IEntity>>(new Map());
  const [storeMenuData, setStoreMenuData] = useState<IStoreMenuQuery>();
  const [storeDayParts, setStoreDayParts] = useState<IValidDayPart[]>([]);
  const [loadedStoreMenuVariant, setLoadedStoreMenuVariant] = useState<
    ILoadedStoreMenuVariant | undefined
  >();
  const storeMenuFetchId = React.useRef(0);
  const {
    setActiveDayParts: setBrazeActiveDayParts,
    setDayPartsSourceResolved: setBrazeDayPartsSourceResolved,
  } = useBrazeContext();

  const [
    getStoreMenuLazy,
    { loading: storeMenuLoading, error: storeMenuError, called: storeMenuCalled },
  ] = useCachedStoreMenuLazyQuery();

  const publishStoreMenuData = useCallback((nextStoreMenuData: IStoreMenuQuery) => {
    setStoreMenuData(nextStoreMenuData);
    setStoreDayParts(oldValue => {
      const newValue = remapDayParts(
        Array.from(nextStoreMenuData.storeMenuWithDayParts?.dayParts ?? [])
      );
      return fastDeepEqual(oldValue, newValue) ? oldValue : newValue;
    });
    setStoreMenuDataMap(oldValue => {
      const newValue = new Map(
        nextStoreMenuData.storeMenuWithDayParts?.entities?.map(menu => [menu.id, menu])
      );
      return fastDeepEqual(oldValue, newValue) ? oldValue : newValue;
    });
  }, []);

  const isMenuLoading =
    isLegacyMenuEnabled &&
    (featureMenuLoading ||
      loading ||
      storeMenuLoading ||
      menuCompletePending ||
      storeMenuCompletePending);

  const posDataServiceMode = isDelivery ? PosDataServiceMode.DELIVERY : PosDataServiceMode.PICKUP;

  const _getStoreMenu: IMainMenuContext['getStoreMenu'] = useCallback(
    async ({ region, storeId, forceRefresh }) => {
      const currentFetchId = ++storeMenuFetchId.current;
      const requestedVariantName = variantName ?? null;
      setLoadedStoreMenuVariant(undefined);
      setStoreMenuData(undefined);
      setStoreMenuDataMap(new Map());
      setStoreDayParts(EMPTY_MENU_DAY_PARTS);
      const variables = {
        channel: Channel.WHITELABEL,
        region,
        storeId,
        serviceMode: posDataServiceMode,
      };

      try {
        let completedRequestedData: IStoreMenuQuery | undefined;
        const { data: returnedRequestedData, error } = await getStoreMenuLazy({
          allowFromCache: !forceRefresh,
          onCompleted: completedData => {
            completedRequestedData = completedData;
          },
          variables: { ...variables, variantName },
        });
        const requestedData = completedRequestedData ?? returnedRequestedData;

        if (currentFetchId !== storeMenuFetchId.current) {
          return;
        }

        // If fetching a variantName returned 404 we'll do a second attempt without variantName
        // Otherwise the user might have a blank menu due to a possible misconfiguration.
        const storeMenuNotFound = Boolean(
          error &&
            parseGraphQLErrorCodes(error).some(
              parsedError => parsedError.errorCode === GraphQLErrorCodes.STORE_MENU_NOT_FOUND
            )
        );
        if (variantName && storeMenuNotFound) {
          logger.warn(`Store Menu not found for variant menu: ${{ ...variables, variantName }}`);
          let completedFallbackData: IStoreMenuQuery | undefined;
          const { data: returnedFallbackData, error: fallbackError } = await getStoreMenuLazy({
            allowFromCache: !forceRefresh,
            onCompleted: completedData => {
              completedFallbackData = completedData;
            },
            variables,
          });
          const fallbackData = completedFallbackData ?? returnedFallbackData;
          if (currentFetchId !== storeMenuFetchId.current) {
            return;
          }
          if (!fallbackError && fallbackData) {
            publishStoreMenuData(fallbackData);
            setLoadedStoreMenuVariant({
              requestedVariantName,
              resolvedVariantName: null,
              storeId,
            });
          }
          return;
        }

        if (!error && requestedData) {
          publishStoreMenuData(requestedData);
          setLoadedStoreMenuVariant({
            requestedVariantName,
            resolvedVariantName: requestedVariantName,
            storeId,
          });
        }
      } finally {
        if (currentFetchId === storeMenuFetchId.current) {
          setStoreMenuCompletePending(false);
        }
      }
    },
    [getStoreMenuLazy, posDataServiceMode, publishStoreMenuData, variantName]
  );
  const getStoreMenu = useStableCallback(_getStoreMenu);
  const [isOffer, setIsOffer] = useState(false);

  const getMainMenu: IMainMenuContext['getMainMenu'] = useCallback(
    ({ menuId, region, storeId, forceRefresh }): boolean => {
      let hasExecutedFetch = false;
      if (!menuId || !region || !storeId) {
        return hasExecutedFetch;
      }

      // Prevent getting menu sections from the cache when the language has changed.
      const languageChanged = prevLanguageRef.current !== language;
      const allowFromCache = !forceRefresh && !languageChanged;

      // These loading flags are set to false on each query callback
      setMenuCompletePending(true);
      setStoreMenuCompletePending(true);

      getMenuSectionsLazy({ allowFromCache, variables: { id: menuId } });
      // getStoreMenu has no separate cache-bypass flag, so fold the language change into
      // forceRefresh too - otherwise a pre-switch response (day parts/entries in the old
      // language) keeps being served.
      getStoreMenu({ region, storeId, forceRefresh: forceRefresh || languageChanged });
      if (forceRefresh) {
        refetchPosData({ storeNumber: storeId });
      }
      hasExecutedFetch = true;
      prevLanguageRef.current = language;

      return hasExecutedFetch;
    },
    [getMenuSectionsLazy, refetchPosData, getStoreMenu, language]
  );

  const getSection = useCallback((sanityId: string) => menuSectionMap.get(sanityId), [
    menuSectionMap,
  ]);

  const getPricingAndAvailability = useCallback(
    (id: string) => {
      if (storeMenuLoading || storeMenuDataMap.size === 0) {
        return null;
      }

      return storeMenuDataMap.get(id);
    },
    [storeMenuDataMap, storeMenuLoading]
  );

  const getCalories = useCallback(
    (item: MenuObject) => {
      if (!item || !item._id) {
        return null;
      }
      return storeMenuDataMap.get(item._id)?.calories || null;
    },
    [storeMenuDataMap]
  );

  const getStoreMenuEntity = useCallback(
    (menuEntityId: string) => {
      if (storeMenuLoading || storeMenuDataMap.size === 0) {
        return null;
      }

      return storeMenuDataMap.get(menuEntityId);
    },
    [storeMenuDataMap, storeMenuLoading]
  );

  const isCurrentStoreMenuLoaded = Boolean(
    storeMenuData?.storeMenuWithDayParts &&
      loadedStoreMenuVariant &&
      loadedStoreMenuVariant.storeId === store?.number &&
      loadedStoreMenuVariant.requestedVariantName === (variantName ?? null)
  );
  // A successful cached fallback does not clear Apollo's failed variant query.
  // The completed request above owns readiness; each new fetch clears it first.
  const hasError = Boolean(error || (storeMenuError && !isCurrentStoreMenuLoaded));
  const isSelectedLegacyMenuLoaded = Boolean(
    menuId &&
      data?.Menu?._id === menuId &&
      data.Menu.options?.length &&
      storeMenuCalled &&
      isCurrentStoreMenuLoaded &&
      storeMenuDataMap.size > 0 &&
      loadedStoreMenuVariant &&
      loadedStoreMenuVariant.resolvedVariantName === loadedStoreMenuVariant.requestedVariantName
  );
  useLogMenuExperimentExposure({
    exposureFlagKey: menuExperimentExposureFlagKey,
    exposurePathname: menuExperimentExposurePathname,
    isVariantDisplayed:
      isLegacyMenuEnabled &&
      isMenuExperimentVariantSelected &&
      isSelectedLegacyMenuLoaded &&
      !isMenuLoading &&
      !hasError,
  });

  // Feature Menu day parts are Legacy-only and stay empty for One Menu stores.
  const featureMenuDayParts = useMemo(() => featureMenu?.dayParts?.filter(isValidDayPart) || [], [
    featureMenu,
  ]);

  const dayParts =
    storeDayPartsEnabled && storeDayParts.length > 0 ? storeDayParts : featureMenuDayParts;
  const selectedStoreDayParts = isUnifiedMenuEnabled
    ? unifiedMenuDayParts
    : isLegacyMenuEnabled
    ? dayParts
    : EMPTY_MENU_DAY_PARTS;
  // Store Information already owns the active window. Parsing menu-service schedules in that
  // case only feeds blank open/close values into the legacy timer.
  const legacyDayPartsForActiveCalculation = useMemo(() => {
    if (!isLegacyMenuEnabled || storeInfoActiveDayParts !== undefined) {
      return EMPTY_MENU_DAY_PARTS;
    }
    return dayParts.filter(isValidDayPart);
  }, [dayParts, isLegacyMenuEnabled, storeInfoActiveDayParts]);

  // `dayParts` can be legitimately empty (this store has none configured) or transiently empty
  // because the source it's currently falling back to hasn't resolved yet. Consumers gating on
  // "day parts are ready" need to tell these apart, so this mirrors `dayParts`'s own
  // source-selection logic: storeDayParts only counts as resolved when it's actually the
  // selected source; otherwise readiness depends on featureMenuDayParts' own loading state.
  const isUsingStoreDayParts = storeDayPartsEnabled && storeDayParts.length > 0;
  // Store Information day parts are a resolved source on their own, independent of Feature Menu.
  const isLegacyMenuDayPartsSourceResolved =
    isLegacyMenuEnabled &&
    (storeInfoActiveDayParts !== undefined || isUsingStoreDayParts || !featureMenuLoading);

  const [legacyMenuActiveDayParts, forceRefreshActiveDayParts] = useActiveDayParts({
    // One Menu owns active-boundary evaluation in UnifiedMenuProvider. Keep this legacy timer
    // inactive while still publishing One Menu schedules to shared loyalty consumers. Store
    // Information likewise owns the active window once it resolves one.
    dayParts: legacyDayPartsForActiveCalculation,
    isEnabled: isLegacyMenuEnabled && storeInfoActiveDayParts === undefined,
  });
  const selectedMenuActiveDayParts = isUnifiedMenuEnabled
    ? unifiedMenuActiveDayParts
    : storeInfoActiveDayParts ?? legacyMenuActiveDayParts;
  const activeDayParts = noStoreSelected ? EMPTY_ACTIVE_DAY_PARTS : selectedMenuActiveDayParts;
  const isDayPartsSourceResolved = isUnifiedMenuEnabled
    ? isUnifiedMenuDayPartsSourceResolved
    : isLegacyMenuDayPartsSourceResolved;

  useEffect(() => {
    setBrazeActiveDayParts(activeDayParts);
  }, [activeDayParts, setBrazeActiveDayParts]);

  // Content cards must not render until day parts are known - showing them earlier means
  // filtering against an activeDayParts value that hasn't resolved yet (see setBrazeActiveDayParts
  // above), which can either wrongly hide day-part-scoped cards or wrongly show excluded ones.
  // isDayPartsSourceResolved itself stays false forever with no store selected (other consumers,
  // like cart availability, rely on that to skip their own checks) - activeDayParts is already
  // forced to its resolved-empty value in that case, so Braze treats it as resolved too.
  useEffect(() => {
    setBrazeDayPartsSourceResolved(noStoreSelected || isDayPartsSourceResolved);
  }, [noStoreSelected, isDayPartsSourceResolved, setBrazeDayPartsSourceResolved]);

  const value = {
    data,
    getStoreMenu,
    getMainMenu,
    getSection,
    hasError,
    loading,
    isMenuLoading,
    storeMenuCalled,
    storeMenuLoading,
    activeDayParts,
    forceRefreshActiveDayParts,
    dayParts: selectedStoreDayParts,
    isDayPartsSourceResolved,
    storeMenuData,
    getPricingAndAvailability,
    getCalories,
    getStoreMenuEntity,
    isOffer,
    setIsOffer,
  };

  return <MainMenuContext.Provider value={value}>{children}</MainMenuContext.Provider>;
};

// MainMenuContext.Provider.whyDidYouRender = true;

export const useMainMenuContext = () => useContext(MainMenuContext);
export default MainMenuContext.Consumer;
